import fmt_date, { format_datetime_dd_mmm_yy as fmt_datetime } from '../../../utils/format_date_dd_mmm_yy';
import React, { useEffect, useRef, useState } from 'react';
import { Badge, Button, Input, Spinner } from 'reactstrap';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';

/**
 * One resolver merge in a finding's history, with the rep's verdict on it.
 *
 * The nightly issue resolver (cs-analysis docs/design-finding-promoter.md
 * §7.1) may decide a freshly extracted candidate is the SAME issue as an
 * existing OPEN finding and append the candidate's references to it. That
 * write is a `FindingHistory evidence_added` row whose `prevState` is the
 * machine-readable merge (`kind: 'resolver_evidence_added'`, the candidate,
 * the refs it added, similarity + method). A wrong merge is invisible
 * damage — the refs land on the wrong finding and the candidate is never
 * ranked — and only the rep can tell, so this card offers two chips:
 *
 *   ✓ Same issue          → POST …/confirm-evidence/:historyId
 *   ✗ Not the same issue  → optional reason → POST …/detach-evidence/:historyId
 *
 * Both are idempotent and last-verdict-wins, so a recorded verdict can be
 * changed (confirm after detach puts the refs back). Every verdict is also
 * a human label for the resolver's eval harness.
 *
 * Verdicts are NOT stored on the merge row: later `evidence_detached` /
 * `evidence_confirmed` rows point back at it via
 * `prevState.evidenceAddedHistoryId`. `latest_merge_verdicts` folds those
 * into a per-merge "current verdict" (newest wins); the caller passes the
 * result in as `verdict` and refetches history after each POST so the new
 * verdict row is what the card shows — no optimistic local copy to drift.
 */

/** True for the history rows the two verdict endpoints accept. Pre-2026-09-12
 *  `evidence_added` rows carry only `{ evidenceCount }` (no `kind`) and render
 *  as plain ledger entries. */
export const is_resolver_merge = (row) =>
    !!row
    && row.changeType === 'evidence_added'
    && !!row.prevState
    && row.prevState.kind === 'resolver_evidence_added';

const VERDICT_KIND_BY_CHANGE_TYPE = {
    evidence_detached: 'detached',
    evidence_confirmed: 'confirmed',
};

const changed_at_ms = (iso) => {
    const t = iso ? Date.parse(iso) : NaN;
    return Number.isNaN(t) ? 0 : t;
};

/**
 * Current verdict per resolver merge row, keyed by the merge row's id:
 *   { [evidenceAddedHistoryId]: { kind: 'detached' | 'confirmed', byUserId, reason, at, rowId } }
 * The newest verdict row (by changedAt) wins regardless of the order the
 * rows arrive in; on an exact timestamp tie the row seen first wins, since
 * the endpoint returns newest-first. Verdict rows without a target are
 * ignored. Rows for merges that are not in `rows` still produce an entry —
 * harmless, nobody looks them up.
 */
export const latest_merge_verdicts = (rows) => {
    const out = {};
    if (!Array.isArray(rows)) return out;
    for (const h of rows) {
        const kind = h ? VERDICT_KIND_BY_CHANGE_TYPE[h.changeType] : null;
        const prev = (h && h.prevState) || {};
        const target = prev.evidenceAddedHistoryId;
        if (!kind || !target) continue;
        const at = h.changedAt || null;
        const cur = out[target];
        if (cur && changed_at_ms(cur.at) >= changed_at_ms(at)) continue;
        out[target] = {
            kind,
            byUserId: h.byUserId || null,
            reason: prev.reason ? String(prev.reason) : null,
            at,
            rowId: h.id,
        };
    }
    return out;
};

/** Resolver merges with no verdict yet — the number on the History tab hint. */
export const count_undecided_merges = (rows) => {
    if (!Array.isArray(rows)) return 0;
    const verdicts = latest_merge_verdicts(rows);
    return rows.filter((h) => is_resolver_merge(h) && !verdicts[h.id]).length;
};

/** "similarity 0.87 via judge" / "exact fingerprint match" / null when the
 *  row carries neither a method nor a similarity. */
export const describe_merge_method = (prev) => {
    const p = prev || {};
    const method = p.method ? String(p.method).toLowerCase() : null;
    if (method === 'exact') return 'exact fingerprint match';
    const sim = typeof p.similarity === 'number' && Number.isFinite(p.similarity) ? p.similarity.toFixed(2) : null;
    if (sim && method) return `similarity ${sim} via ${method}`;
    if (sim) return `similarity ${sim}`;
    if (method) return `via ${method}`;
    return null;
};

/** "3 references added (WHATSAPP ×2 · TICKET ×1)" — the count plus a
 *  per-source breakdown in first-seen order. */
export const summarize_added_refs = (addedRefs) => {
    const refs = Array.isArray(addedRefs) ? addedRefs : [];
    const n = refs.length;
    const head = `${n} reference${n === 1 ? '' : 's'} added`;
    const by_source = [];
    for (const r of refs) {
        const s = (r && r.source) || 'unknown';
        const hit = by_source.find((b) => b.source === s);
        if (hit) hit.n += 1;
        else by_source.push({ source: s, n: 1 });
    }
    if (by_source.length === 0) return head;
    return `${head} (${by_source.map((b) => `${b.source} ×${b.n}`).join(' · ')})`;
};

const CHIP = {
    confirmed: { label: '✓ Same issue', color: '#2e9e5b' },
    detached: { label: '✗ Not the same issue', color: '#c0392b' },
};

/**
 * Props:
 *   row            — the `evidence_added` FindingHistory row (must satisfy is_resolver_merge)
 *   verdict        — latest_merge_verdicts(rows)[row.id] or undefined
 *   store_id, finding_id
 *   on_view(next)  — receives the finding view the endpoint returns (same shape as
 *                    GET :id); the drawer merges it into its state so the evidence
 *                    count updates on detach / restore
 *   reload_history — async; called after a verdict is recorded so the timeline
 *                    (and this card's `verdict`) redraws from the server
 */
const ResolverMergeCard = ({ row, verdict, store_id, finding_id, on_view, reload_history }) => {
    const prev = row.prevState || {};
    const candidate = prev.candidate || {};
    const added = Array.isArray(prev.addedRefs) ? prev.addedRefs : [];
    const method = describe_merge_method(prev);
    const [mode, set_mode] = useState('idle'); // idle | noting | busy
    const [reason, set_reason] = useState('');
    const [expanded, set_expanded] = useState(false);
    const [err, set_err] = useState(null);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    const submit = async (kind) => {
        if (mode === 'busy') return;
        set_mode('busy');
        set_err(null);
        const action = kind === 'detached' ? 'detach-evidence' : 'confirm-evidence';
        const trimmed = reason.trim();
        try {
            const next = await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/findings/${encodeURIComponent(finding_id)}/${action}/${encodeURIComponent(row.id)}`,
                method: 'POST',
                body: kind === 'detached' && trimmed ? { reason: trimmed } : {},
            });
            if (typeof on_view === 'function') on_view(next);
            // The verdict row lives in history — refetch so `verdict` is the
            // server's, then drop the spinner.
            if (typeof reload_history === 'function') await reload_history();
            if (!mounted.current) return;
            set_reason('');
            set_mode('idle');
        } catch (e) {
            if (!mounted.current) return;
            set_mode('idle');
            set_err((e && e.message) || String(e));
        }
    };

    const chip = (kind) => {
        const { label, color } = CHIP[kind];
        const active = !!verdict && verdict.kind === kind;
        let title;
        if (active) title = 'Recorded — click the other chip to change it';
        else if (kind === 'confirmed') title = verdict ? 'Undo the detach — put the merged evidence back on this finding' : 'The candidate is this same issue — keep its evidence here';
        else title = verdict ? 'Detach this evidence after all' : "Wrong merge — remove the candidate's evidence from this finding";
        return (
            <span
                key={kind}
                title={title}
                onClick={() => {
                    if (active || mode === 'busy') return;
                    if (kind === 'confirmed') submit('confirmed');
                    else set_mode('noting');
                }}
                style={{
                    fontSize: 10.5, whiteSpace: 'nowrap', borderRadius: 10, padding: '1px 8px',
                    cursor: active ? 'default' : 'pointer', fontWeight: active ? 700 : 500,
                    border: `1px solid ${color}`, color: active ? '#fff' : color, background: active ? color : '#fff',
                }}
            >
                {label}
            </span>
        );
    };

    return (
        <div
            style={{
                padding: '8px 10px',
                borderLeft: `2px solid ${verdict ? (verdict.kind === 'confirmed' ? '#2e9e5b' : '#c0392b') : '#f39c12'}`,
                background: verdict ? '#fafbfc' : '#fffaf0',
                borderRadius: '0 4px 4px 0',
                marginBottom: 6,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <Badge color='warning' style={{ fontWeight: 400 }}>evidence_added</Badge>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#2c3e50' }}>
                    Merged evidence from a new candidate on {fmt_date(row.changedAt)}
                </span>
                {method ? <span style={{ fontSize: 11, color: '#7f8c8d' }}>({method})</span> : null}
                <span style={{ fontSize: 11, color: '#95a5a6' }} title={fmt_datetime(row.changedAt)}>· issue resolver</span>
            </div>

            <div style={{ fontSize: 12, color: '#34495e', marginTop: 4, display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
                <span style={{ color: '#7f8c8d' }}>Candidate:</span>
                <strong>{candidate.title || <span style={{ color: '#95a5a6', fontWeight: 400 }}>(untitled)</span>}</strong>
                {candidate.module ? (
                    <Badge color='light' style={{ color: '#2c3e50', fontWeight: 400, fontSize: 10 }}>{candidate.module}</Badge>
                ) : null}
                {candidate.observation || candidate.mechanism ? (
                    <span
                        onClick={() => set_expanded((v) => !v)}
                        style={{ fontSize: 11, color: '#3498db', cursor: 'pointer', userSelect: 'none' }}
                    >
                        {expanded ? 'hide details ▾' : 'show details ▸'}
                    </span>
                ) : null}
            </div>
            {expanded ? (
                <div style={{ marginTop: 4, padding: '6px 10px', background: '#fff', borderLeft: '3px solid #f1c40f', fontSize: 12, color: '#34495e' }}>
                    {candidate.observation ? (
                        <div>
                            <span style={{ fontSize: 10, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5 }}>Observation</span>
                            <div style={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>{candidate.observation}</div>
                        </div>
                    ) : null}
                    {candidate.mechanism ? (
                        <div style={{ marginTop: candidate.observation ? 6 : 0 }}>
                            <span style={{ fontSize: 10, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5 }}>Mechanism</span>
                            <div style={{ whiteSpace: 'pre-wrap' }}>{candidate.mechanism}</div>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {verdict ? (
                <div style={{ fontSize: 11, marginTop: 6, color: verdict.kind === 'confirmed' ? '#2e9e5b' : '#c0392b' }}>
                    {verdict.kind === 'confirmed' ? '✓ confirmed — same issue' : '✗ detached — not the same issue'}
                    {verdict.byUserId ? ` · by ${verdict.byUserId}` : ''}
                    {verdict.at ? ` · ${fmt_datetime(verdict.at)}` : ''}
                    {verdict.reason ? <span style={{ color: '#7f8c8d' }}> — {verdict.reason}</span> : null}
                </div>
            ) : null}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: '#7f8c8d' }} title={typeof prev.evidenceCount === 'number' ? `${prev.evidenceCount} evidence entries before the merge` : undefined}>
                    {summarize_added_refs(added)}
                    {verdict && verdict.kind === 'detached' ? ' · removed from this finding' : ''}
                </span>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    {mode === 'busy' ? <Spinner size='sm' style={{ width: 12, height: 12 }} /> : (
                        <>
                            {chip('confirmed')}
                            {chip('detached')}
                        </>
                    )}
                </div>
            </div>

            {mode === 'noting' ? (
                <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                    <Input
                        bsSize='sm'
                        value={reason}
                        placeholder='Why is it not the same issue? (optional)'
                        autoFocus
                        maxLength={2000}
                        onChange={(e) => set_reason(e.target.value)}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') { e.preventDefault(); submit('detached'); }
                            else if (e.key === 'Escape') { e.preventDefault(); set_mode('idle'); }
                        }}
                        style={{ fontSize: 11, padding: '2px 6px', height: 24 }}
                    />
                    <Button size='sm' color='danger' outline style={{ fontSize: 11, padding: '0 8px', whiteSpace: 'nowrap' }} onClick={() => submit('detached')}>Detach</Button>
                    <Button size='sm' color='light' style={{ fontSize: 11, padding: '0 8px' }} onClick={() => set_mode('idle')}>Cancel</Button>
                </div>
            ) : null}
            {err ? <div style={{ fontSize: 11, color: '#c0392b', marginTop: 4 }}>{err}</div> : null}
        </div>
    );
};

export default ResolverMergeCard;
